import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { Badge } from '../../../badge/src';
import { Dropdown } from '../../../dropdown/src';
import { useLocalizeMessage } from '../../../i18n/src';
import { Link } from '../../../link/src';
import { Logo } from '../../../logo/src';
import { Toggle } from '../../../toggle/src';
import { Text } from '../../../typography/src';
import { useColorScheme } from './use_color_scheme.hook';
// useLayoutEffect avoids a flash of the wrong layout on the client, but warns
// during SSR — fall back to useEffect when there is no DOM.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
const defaultRenderLink = ({ name, path, linkStyles, isActive, onClick, LinkComponent }) => {
    return (_jsx(Link, { href: path, variant: 'custom', customStyles: linkStyles, "aria-current": isActive ? 'page' : undefined, onClick: onClick, LinkComponent: LinkComponent, children: name }, path));
};
const isModifiedNavigationClick = (event) => Boolean(event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1));
// DropdownMenuItem handles the click on a wrapping div and unmounts the menu before a nested
// anchor's default action can run. Dispatch a click on the rendered link so LinkComponent (Next.js
// Link, React Router) still performs client navigation, and report whether that click owned it.
const activateRenderedLink = (link) => {
    let handledByLink = false;
    const noteUnhandledDefault = (nativeEvent) => {
        if (nativeEvent.defaultPrevented) {
            handledByLink = true;
            return;
        }
        nativeEvent.preventDefault();
    };
    window.addEventListener('click', noteUnhandledDefault);
    link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    window.removeEventListener('click', noteUnhandledDefault);
    return handledByLink;
};
const getLinkStyles = (isActive) => {
    return `nucleus-box-border nucleus-border-solid nucleus-shrink-0 nucleus-text-sm nucleus-border-0 nucleus-border-b-2 nucleus-no-underline nucleus-px-2 nucleus-py-[0.9375rem] focus-visible:nucleus-outline-none focus-visible:nucleus-ring-2 focus-visible:nucleus-ring-primary-500 focus-visible:nucleus-ring-offset-1 ${isActive
        ? 'nucleus-text-text-primary nucleus-font-medium nucleus-border-primary-500 hover:nucleus-text-text-primary'
        : 'nucleus-text-text-secondary nucleus-border-transparent hover:nucleus-border-opacity-100 hover:nucleus-text-text-primary hover:nucleus-border-neutral-300'}`;
};
/** The Header component renders a logo image, navigational links, and a clickable
 * avatar that presents a dropdown, all of which are configurable via the component's props. */
export const Header = ({ navLinks = [], activeLinkPath, logoSrc, dropdownOptions = [], renderLink = defaultRenderLink, onSignOut, workspaceOptions, selectedWorkspace, switcherEnabled = false, platformVersion, showColorSchemeToggle = true, notificationsConfig, user, }) => {
    const hasLinks = navLinks.length > 0;
    const translate = useLocalizeMessage();
    const { isDarkMode, setColorScheme } = useColorScheme();
    const headerRef = useRef(null);
    // When true the inline nav is collapsed into the hamburger menu and the
    // workspace switcher moves into the avatar dropdown.
    const [isCompact, setIsCompact] = useState(false);
    // The full-size (expanded) width the header needs. Recorded when the expanded
    // layout overflows, so we know exactly when there is room to expand again.
    const requiredWidthRef = useRef(null);
    // A stable signature so inline-defined navLinks arrays don't cause churn.
    const navSignature = useMemo(() => navLinks.map((link) => link.path).join('|'), [navLinks]);
    // Reset the measurement whenever the set of links changes.
    useIsomorphicLayoutEffect(() => {
        requiredWidthRef.current = null;
        setIsCompact(false);
    }, [navSignature]);
    // Collapse to the hamburger only when the full-size header can't fit, and
    // expand again once there's room — rather than shrinking the nav items.
    useIsomorphicLayoutEffect(() => {
        if (!hasLinks || typeof ResizeObserver === 'undefined') {
            return;
        }
        const el = headerRef.current;
        if (!el) {
            return;
        }
        const measure = () => {
            if (!isCompact) {
                if (el.scrollWidth > el.clientWidth + 1) {
                    requiredWidthRef.current = el.scrollWidth;
                    setIsCompact(true);
                }
            }
            else if (requiredWidthRef.current != null && el.clientWidth >= requiredWidthRef.current) {
                setIsCompact(false);
            }
        };
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        measure();
        return () => observer.disconnect();
    }, [hasLinks, isCompact, navSignature]);
    const avatarMenuOptions = useMemo(() => {
        var _a;
        const items = [];
        if (platformVersion) {
            items.push({
                id: 'platform-version',
                content: (_jsx(Text, { className: 'nucleus-text-text-disabled', size: 'sm', children: platformVersion })),
                readonly: true,
                hasBottomBorder: true,
            });
        }
        // Notifications is a regular avatar-menu item (no standalone bell in the header).
        if (notificationsConfig === null || notificationsConfig === void 0 ? void 0 : notificationsConfig.enabled) {
            const unreadCount = (_a = notificationsConfig.unreadCount) !== null && _a !== void 0 ? _a : 0;
            items.push({
                id: 'notifications',
                content: (_jsxs("div", { className: 'nucleus-flex nucleus-items-center nucleus-gap-2', children: [_jsx("span", { children: translate('Notifications') }), unreadCount > 0 && _jsx(Badge, { variant: 'common', label: unreadCount > 99 ? '99+' : String(unreadCount) })] })),
                onClick: ({ closeDropdown }) => {
                    notificationsConfig.onClick();
                    closeDropdown === null || closeDropdown === void 0 ? void 0 : closeDropdown();
                },
            });
        }
        // In compact mode the workspace switcher lives inside the avatar menu.
        if (isCompact && switcherEnabled && workspaceOptions && selectedWorkspace) {
            items.push({
                id: 'workspace-heading',
                readonly: true,
                content: (_jsx("span", { className: 'nucleus-text-xs nucleus-font-medium nucleus-text-text-secondary', children: translate('Workspace') })),
            });
            workspaceOptions.forEach((option, index) => {
                items.push(Object.assign(Object.assign({}, option), { hasBottomBorder: index === workspaceOptions.length - 1 ? true : option.hasBottomBorder }));
            });
        }
        items.push(...dropdownOptions);
        if (showColorSchemeToggle) {
            items.push({
                id: 'color-scheme',
                content: (_jsx("div", { className: 'nucleus-w-full', children: _jsx(Toggle, { checked: isDarkMode, dataTestId: 'color-scheme-toggle', label: translate('Dark mode'), onChange: (checked) => setColorScheme(checked ? 'dark' : 'light') }) })),
                hasTopBorder: true,
                readonly: true,
            });
        }
        items.push({
            id: 'sign-out',
            content: _jsx("div", { children: translate('Sign out') }),
            hasTopBorder: true,
            onClick: onSignOut,
        });
        return items;
    }, [
        dropdownOptions,
        isCompact,
        isDarkMode,
        switcherEnabled,
        workspaceOptions,
        selectedWorkspace,
        platformVersion,
        setColorScheme,
        showColorSchemeToggle,
        notificationsConfig,
        onSignOut,
        translate,
    ]);
    // NOTE: don't look at this too closely, there is currently no explicit design for the mobile link menu.
    // most likely it'll be a side drawer in the future and this logic will change.
    const mobileNavLinkOptions = useMemo(() => navLinks.map(({ name, path, onClick, LinkComponent }) => {
        const isActive = path === activeLinkPath;
        let isActivatingLink = false;
        const onDropdownClick = ({ closeDropdown, event }) => {
            var _a, _b, _c;
            if (isActivatingLink) {
                return;
            }
            if (isModifiedNavigationClick(event)) {
                closeDropdown === null || closeDropdown === void 0 ? void 0 : closeDropdown();
                return;
            }
            onClick === null || onClick === void 0 ? void 0 : onClick();
            const linkElement = (_b = (_a = event === null || event === void 0 ? void 0 : event.currentTarget) === null || _a === void 0 ? void 0 : _a.querySelector('[data-header-nav-link]')) !== null && _b !== void 0 ? _b : (_c = event === null || event === void 0 ? void 0 : event.currentTarget) === null || _c === void 0 ? void 0 : _c.querySelector('a');
            const activatedFromLink = (event === null || event === void 0 ? void 0 : event.target) instanceof Node && Boolean(linkElement === null || linkElement === void 0 ? void 0 : linkElement.contains(event.target));
            let handledByLink = Boolean(activatedFromLink && (event === null || event === void 0 ? void 0 : event.defaultPrevented));
            if (!activatedFromLink && linkElement) {
                isActivatingLink = true;
                handledByLink = activateRenderedLink(linkElement);
                isActivatingLink = false;
            }
            const consumerOwnsNavigation = Boolean(onClick) && !LinkComponent;
            if (!handledByLink && !consumerOwnsNavigation) {
                event === null || event === void 0 ? void 0 : event.preventDefault();
                const href = linkElement instanceof HTMLAnchorElement && linkElement.href ? linkElement.href : path;
                window.location.assign(href);
            }
            else if (!handledByLink) {
                event === null || event === void 0 ? void 0 : event.preventDefault();
            }
            closeDropdown === null || closeDropdown === void 0 ? void 0 : closeDropdown();
        };
        return {
            id: path,
            content: (_jsx(Link, { href: path, variant: 'custom', customStyles: 'nucleus-block nucleus-w-full nucleus-text-inherit nucleus-no-underline nucleus-font-inherit', "aria-current": isActive ? 'page' : undefined, "data-header-nav-link": '', LinkComponent: LinkComponent, children: name })),
            onClick: onDropdownClick,
        };
    }), [activeLinkPath, navLinks]);
    return (_jsxs("header", { ref: headerRef, className: 'nucleus-box-border nucleus-font-sans nucleus-relative nucleus-flex nucleus-w-full nucleus-items-center nucleus-bg-background nucleus-border-0 nucleus-border-b nucleus-border-solid nucleus-border-neutral-200 dark:nucleus-border-neutral-300 nucleus-px-6 focus-visible:nucleus-outline-none', children: [_jsxs("div", { className: `nucleus-box-border nucleus-flex nucleus-shrink-0 nucleus-items-center nucleus-gap-3 ${hasLinks && !isCompact ? 'nucleus-w-64' : ''}`, children: [hasLinks && isCompact && (_jsxs("div", { className: 'nucleus-box-border nucleus-flex nucleus-items-center', children: [_jsx("span", { className: 'nucleus-sr-only', children: translate('Open main menu') }), _jsx(Dropdown, { label: translate('portal links'), variant: 'icon', iconName: 'hamburgerMenu', items: mobileNavLinkOptions, position: 'bottom', selectorClassName: 'nucleus-rounded-md nucleus-p-1 nucleus-text-text-primary hover:nucleus-bg-neutral-100' })] })), typeof logoSrc === 'string' ? (_jsx("img", { src: logoSrc, alt: 'Medable logo', className: 'nucleus-h-8 nucleus-py-3 nucleus-min-w-8' })) : (_jsx("div", { className: `nucleus-flex nucleus-h-8 nucleus-box-content nucleus-items-center nucleus-justify-start nucleus-py-3 ${isCompact ? 'nucleus-w-8 nucleus-min-w-8' : 'nucleus-w-32'}`, children: logoSrc || _jsx(Logo, { wordless: isCompact }) }))] }), hasLinks && !isCompact && (_jsx("nav", { className: 'nucleus-box-border nucleus-flex nucleus-shrink-0 nucleus-items-center nucleus-gap-6', children: navLinks.map(({ name, path, LinkComponent }) => {
                    const isActive = path === activeLinkPath;
                    const linkStyles = getLinkStyles(isActive);
                    return renderLink({ name, path, linkStyles, isActive, LinkComponent });
                }) })), _jsx("div", { className: 'nucleus-grow' }), _jsxs("div", { className: 'nucleus-flex nucleus-shrink-0 nucleus-items-center nucleus-gap-4', children: [!isCompact && workspaceOptions && selectedWorkspace && (_jsxs("div", { className: 'nucleus-flex nucleus-items-center nucleus-border-0 nucleus-border-r nucleus-border-solid nucleus-h-10 nucleus-pr-4 nucleus-border-neutral-300 nucleus-text-text-primary', children: [!switcherEnabled && _jsx(Text, { size: 'sm', children: selectedWorkspace }), switcherEnabled && (_jsx(Dropdown, { items: workspaceOptions, label: selectedWorkspace, position: 'bottom-end', selectorClassName: 'nucleus-border-0 nucleus-gap-4', variant: 'default', width: 'full' }))] })), _jsx(Dropdown, { label: translate('user avatar'), variant: 'avatar', items: avatarMenuOptions, position: 'bottom-end', user: user })] })] }));
};
//# sourceMappingURL=header.component.js.map